import React from 'react';
import { formatTimeFromMinutes } from '../utils/helpers';
import { SCHEDULE_START_MINUTE, TOTAL_SCHEDULE_MINUTES } from '../config/constants';

// Define colors for the overlay (using semi-transparent backgrounds)
const AVAILABILITY_COLORS = {
    red: 'bg-red-400/10', // Semi-transparent red
    orange: 'bg-orange-300/50', // Semi-transparent orange
    green: 'bg-transparent' // No overlay for available slots
};

interface AvailabilityStatus {
    teacherStatus: 'red' | 'orange' | 'green';
    groupStatus: 'red' | 'orange' | 'green';
    isRoomOccupied: boolean;
    isRoomValidForSubject: boolean;
    overallStatus: 'red' | 'orange' | 'green';
    conflictReasons: Array<{ reason: string; severity: 'red' | 'orange' | 'green' }>;
    conflictReason?: string;
}

interface AvailabilityTransparentOverlaysProps {
    isAdmin: boolean;
    availabilityOverlayData?: Map<string, AvailabilityStatus> | undefined;
    day: string;
    roomIndexMap: Map<string, number>;
    totalGridContentHeight: number;
    gridHeaderHeight: number;
    checkIntervalMinutes: number;
    KEY_SEPARATOR: string;
}

const AvailabilityTransparentOverlays: React.FC<AvailabilityTransparentOverlaysProps> = ({
    isAdmin,
    availabilityOverlayData,
    day,
    roomIndexMap,
    totalGridContentHeight,
    gridHeaderHeight,
    checkIntervalMinutes,
    KEY_SEPARATOR,
}) => {
    if (!isAdmin || !availabilityOverlayData) {
        return null;
    }

    return (
        <>
            {Array.from<[string, AvailabilityStatus]>(availabilityOverlayData.entries()).map(([key, status]) => {
                const keyParts = key.split(KEY_SEPARATOR);
                // Only render if the key matches the current day
                if (keyParts.length !== 3 || keyParts[0] !== day) return null;
                const roomName = keyParts[1];
                const timeMinutesStr = keyParts[2];

                // Add null checks for strict TypeScript
                if (!roomName || !timeMinutesStr) return null;

                const timeMinutes = parseInt(timeMinutesStr, 10);
                const roomIndex = roomIndexMap.get(roomName);
                if (roomIndex === undefined || isNaN(timeMinutes)) return null;

                const topPosition = (((timeMinutes - SCHEDULE_START_MINUTE) / TOTAL_SCHEDULE_MINUTES) * totalGridContentHeight) + gridHeaderHeight;
                const height = (totalGridContentHeight * checkIntervalMinutes / TOTAL_SCHEDULE_MINUTES);
                const minHeightPx = 5;
                const calculatedHeightPx = Math.max(minHeightPx, height);
                const isRed = status.overallStatus === 'red';
                const reasons = status.conflictReasons || [];
                const isOnlyRoomOccupancy = isRed && reasons.length === 1 && reasons[0]?.reason?.startsWith('Room (') && reasons[0]?.reason?.includes(' occupied by ');
                if (isOnlyRoomOccupancy) return null; // Don't show overlay just for room occupancy

                return (
                    <div
                        key={key}
                        className={`absolute pointer-events-none ${AVAILABILITY_COLORS[status.overallStatus]} rounded-sm`}
                        title={`${roomName} @ ${formatTimeFromMinutes(timeMinutes)}: ${status.conflictReason || status.overallStatus}`}
                        style={{
                            top: `${topPosition}px`,
                            height: `${calculatedHeightPx}px`,
                            gridColumnStart: roomIndex + 2,
                            gridColumnEnd: roomIndex + 3,
                            width: '100%',
                            zIndex: 5
                        }}
                    />
                );
            })}
        </>
    );
};

export default AvailabilityTransparentOverlays; 